import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Footer } from 'components/Footer';
import Header from 'components/Header';
import { PATHS } from 'routes/paths';
import { isValidEmail } from 'utils/validation';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const stripedBg = {
    backgroundImage:
      'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)',
  };

  const validate = () => {
    let isValid = true;

    if (!email.trim()) {
      setError('Email address is required');
      isValid = false;
    } else if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      isValid = false;
    } else {
      setError('');
    }

    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (validate()) {
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSuccess(true);
      }, 1500);
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={stripedBg} className="min-h-screen w-full py-2 px-4 sm:px-8 lg:px-12 flex justify-center font-sans text-slate-900 overflow-hidden">
      <div
        className="w-full bg-white rounded-[2.5rem] overflow-hidden border border-gray-200 flex flex-col shadow-sm"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow flex items-center justify-center py-24 px-4">
          <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {isSuccess ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-[#2b3543]/10 text-[#2b3543] rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Check your email</h2>
                <p className="text-slate-500 font-medium mb-8 leading-relaxed">
                  We&apos;ve sent a password reset link to <br />
                  <span className="font-bold text-slate-900">{email}</span>
                </p>
                <Link
                  to={PATHS.login}
                  className="inline-block w-full bg-[#2b3543] text-white py-4 rounded-xl font-bold tracking-wide hover:bg-[#1e2631] transition-colors shadow-sm"
                >
                  RETURN TO LOGIN
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <div
                    className="w-12 h-12 bg-gray-50 text-slate-900 rounded-xl flex items-center justify-center mx-auto mb-6 border border-gray-200 shadow-sm"
                    style={{ boxShadow: 'var(--shadow-box)' }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      ></path>
                    </svg>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">Reset Password</h1>
                  <p className="text-slate-500 font-medium">
                    Enter your email address and we&apos;ll send you a link to reset your password.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Email <span className="text-slate-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleChange}
                      autoComplete="email"
                      className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 placeholder-gray-400 focus:outline-none focus:ring-1 transition-all ${
                        error
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                      }`}
                    />
                    {error && <p className="text-xs font-bold text-red-500 mt-1">{error}</p>}
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#2b3543] text-white py-4 rounded-xl font-bold tracking-wide hover:bg-[#1e2631] transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center h-[56px]"
                    >
                      {isSubmitting ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        'SEND RESET LINK'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-8 text-center">
                  <Link
                    to={PATHS.login}
                    className="text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
